import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiFetch from "../../../shared/services/apiClient";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg0:"#0f172a", bg1:"#131929", bg2:"#1a2235", bg3:"#1e2a3a",
  border:"rgba(255,255,255,0.07)", accent:"#f97316",
  text:"#f1f5f9", muted:"#64748b",
};

// ── Primitives ────────────────────────────────────────────────────────────────
function Input({ label, required, error, type="text", ...props }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ marginBottom:14 }}>
      {label && (
        <label style={{ display:"block", fontSize:"0.7rem", fontWeight:700,
          letterSpacing:"0.6px", textTransform:"uppercase", marginBottom:5,
          color: error ? "#f87171" : C.muted }}>
          {label}{required && <span style={{ color:"#f87171", marginLeft:3 }}>*</span>}
        </label>
      )}
      <input type={type} {...props}
        onFocus={e => { setFocus(true); props.onFocus?.(e); }}
        onBlur={e => { setFocus(false); props.onBlur?.(e); }}
        style={{ width:"100%", background:C.bg1, borderRadius:8,
          border:`1px solid ${error?"#f87171":focus?C.accent:C.border}`,
          padding:"10px 14px", color:C.text, fontSize:"0.88rem",
          outline:"none", boxSizing:"border-box",
          transition:"border-color 0.15s" }}/>
      {error && <div style={{ color:"#f87171", fontSize:"0.75rem", marginTop:4 }}>{error}</div>}
    </div>
  );
}

function Select({ label, required, error, children, ...props }) {
  return (
    <div style={{ marginBottom:14 }}>
      {label && (
        <label style={{ display:"block", fontSize:"0.7rem", fontWeight:700,
          letterSpacing:"0.6px", textTransform:"uppercase", marginBottom:5,
          color: error ? "#f87171" : C.muted }}>
          {label}{required && <span style={{ color:"#f87171", marginLeft:3 }}>*</span>}
        </label>
      )}
      <select {...props} style={{ width:"100%", background:C.bg1, borderRadius:8,
        border:`1px solid ${error?"#f87171":C.border}`,
        padding:"10px 14px", color:C.text, fontSize:"0.88rem",
        outline:"none", boxSizing:"border-box" }}>
        {children}
      </select>
      {error && <div style={{ color:"#f87171", fontSize:"0.75rem", marginTop:4 }}>{error}</div>}
    </div>
  );
}

function Btn({ children, onClick, variant="primary", disabled, small, type="button" }) {
  const [h, setH] = useState(false);
  const cfg = {
    primary:{ bg:h?"#ea6b10":"#f97316", color:"#fff", border:"#f97316" },
    ghost:  { bg:h?"rgba(255,255,255,0.05)":"transparent", color:C.muted, border:C.border },
    danger: { bg:h?"rgba(239,68,68,0.2)":"rgba(239,68,68,0.08)", color:"#f87171", border:"rgba(239,68,68,0.3)" },
    success:{ bg:h?"#059669":"#10b981", color:"#fff", border:"#10b981" },
  }[variant] ?? {};
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:8,
        color:cfg.color, padding:small?"5px 12px":"9px 18px",
        fontSize:small?"0.78rem":"0.85rem", fontWeight:600,
        cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1,
        transition:"all 0.15s", display:"inline-flex", alignItems:"center",
        gap:7, whiteSpace:"nowrap", fontFamily:"'Inter',sans-serif" }}>
      {children}
    </button>
  );
}

function Toast({ msg, type, onClose }) {
  if (!msg) return null;
  const ok = type !== "error";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10,
      background:ok?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",
      border:`1px solid ${ok?"rgba(16,185,129,0.25)":"rgba(239,68,68,0.25)"}`,
      borderLeft:`3px solid ${ok?"#10b981":"#ef4444"}`,
      borderRadius:8, padding:"10px 16px", marginBottom:16,
      color:ok?"#6ee7b7":"#fca5a5", fontSize:"0.84rem" }}>
      <i className={`fa-solid ${ok?"fa-circle-check":"fa-circle-exclamation"}`}/>
      <span style={{ flex:1 }}>{msg}</span>
      <button onClick={onClose} style={{ background:"none", border:"none", color:"inherit",
        cursor:"pointer", fontSize:"0.9rem", padding:0 }}>
        <i className="fa-solid fa-xmark"/>
      </button>
    </div>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000,
      background:"rgba(0,0,0,0.7)", backdropFilter:"blur(3px)",
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={onClose}>
      <div style={{ background:C.bg2, border:`1px solid ${C.border}`,
        borderRadius:12, width:"min(540px,95vw)", maxHeight:"90vh",
        overflow:"auto", padding:28 }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between",
          alignItems:"center", marginBottom:20 }}>
          <h3 style={{ margin:0, color:C.text, fontSize:"1.1rem",
            fontFamily:"'Manrope',sans-serif", fontWeight:700 }}>{title}</h3>
          <button onClick={onClose}
            style={{ background:"none", border:"none", color:C.muted,
              cursor:"pointer", fontSize:"1.1rem" }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Condominios Tab ───────────────────────────────────────────────────────────
function CondominiosTab() {
  const [condominios, setCond] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | {id, ...}
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ msg:"", type:"" });
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/api/core/admin/condominios/")
      .then(r => r.json())
      .then(d => setCond(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg:"", type:"" }), 4000);
  };

  function openCreate() {
    setForm({ cedulaJuridica:"", nombre:"", email:"", direccion:"" });
    setErrors({});
    setModal("create");
  }

  function openEdit(c) {
    setForm({ ...c });
    setErrors({});
    setModal(c);
  }

  async function handleSave() {
    const errs = {};
    if (!form.cedulaJuridica?.trim()) errs.cedulaJuridica = "Requerido";
    if (!form.nombre?.trim())         errs.nombre = "Requerido";
    if (!form.email?.trim())          errs.email = "Requerido";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/condominios/${modal.id}/` : "/api/core/admin/condominios/";
      const method = isEdit ? "PATCH" : "POST";
      const res    = await apiFetch(url, { method, body: JSON.stringify(form) });
      if (!res.ok) {
        const e = await res.json();
        setErrors(e);
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Condominio actualizado" : "Condominio creado ✓");
    } catch(e) { showToast(e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(c) {
    setDeleting(c.id);
    try {
      const res = await apiFetch(`/api/core/admin/condominios/${c.id}/`, { method:"DELETE" });
      if (!res.ok) { showToast("Error al eliminar", "error"); return; }
      load();
      showToast(`"${c.nombre}" eliminado`);
    } finally { setDeleting(null); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p=>({...p,[key]:e.target.value})) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast({msg:"",type:""})}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <span style={{ color:C.muted, fontSize:"0.85rem" }}>{condominios.length} condominios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-plus"/>Nuevo condominio</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color:C.accent, marginRight:8 }}/>Cargando…
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {condominios.map(c => (
            <div key={c.id} style={{ background:C.bg1, border:`1px solid ${C.border}`,
              borderRadius:10, padding:"14px 18px",
              display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
                background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.2)",
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className="fa-solid fa-building" style={{ color:C.accent }}/>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, color:C.text, fontSize:"0.92rem" }}>{c.nombre}</div>
                <div style={{ color:C.muted, fontSize:"0.77rem", marginTop:2 }}>
                  {c.cedulaJuridica} · {c.email}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <Btn small variant="ghost" onClick={()=>openEdit(c)}>
                  <i className="fa-solid fa-pencil"/>Editar
                </Btn>
                <Btn small variant="danger" disabled={deleting===c.id}
                  onClick={()=>{ if(window.confirm(`¿Eliminar "${c.nombre}"? Esta acción no se puede deshacer.`)) handleDelete(c); }}>
                  {deleting===c.id
                    ? <i className="fa-solid fa-circle-notch fa-spin"/>
                    : <i className="fa-solid fa-trash"/>}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal==="create"?"Nuevo Condominio":"Editar Condominio"} onClose={()=>setModal(null)}>
          <Input label="Nombre" required {...f("nombre")} error={errors.nombre} placeholder="Residencial Los Pinos"/>
          <Input label="Cédula Jurídica" required {...f("cedulaJuridica")} error={errors.cedulaJuridica} placeholder="3-102-000000"/>
          <Input label="Email" required type="email" {...f("email")} error={errors.email} placeholder="admin@condominio.com"/>
          <Input label="Dirección" {...f("direccion")} placeholder="San José, Costa Rica"/>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
            <Btn variant="ghost" onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Usuarios Tab ──────────────────────────────────────────────────────────────
function UsuariosTab() {
  const [usuarios, setUsuarios] = useState([]);
  const [condominios, setCond] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ msg:"", type:"" });
  const [showPw, setShowPw] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiFetch("/api/core/admin/usuarios/").then(r=>r.json()),
      apiFetch("/api/core/admin/condominios/").then(r=>r.json()),
    ]).then(([u, c]) => {
      setUsuarios(Array.isArray(u) ? u : []);
      setCond(Array.isArray(c) ? c : []);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg:"", type:"" }), 4000);
  };

  function openCreate() {
    setForm({ username:"", password:"", email:"", condominio_id:"", is_superuser:false });
    setErrors({});
    setModal("create");
    setShowPw(false);
  }

  function openEdit(u) {
    setForm({ ...u, password:"", condominio_id: u.condominio_id ?? "" });
    setErrors({});
    setModal(u);
    setShowPw(false);
  }

  async function handleSave() {
    const errs = {};
    if (!form.username?.trim()) errs.username = "Requerido";
    if (modal==="create" && !form.password?.trim()) errs.password = "Requerido";
    if (modal==="create" && form.password && form.password.length < 8) errs.password = "Mínimo 8 caracteres";
    if (!form.is_superuser && !form.condominio_id) errs.condominio_id = "Requerido para usuarios normales";
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const isEdit = modal !== "create";
      const url    = isEdit ? `/api/core/admin/usuarios/${modal.id}/` : "/api/core/admin/usuarios/";
      const method = isEdit ? "PATCH" : "POST";
      const body   = { ...form };
      if (isEdit && !body.password) delete body.password; // no enviar password vacío en edición
      const res = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        const e = await res.json();
        setErrors(typeof e === "object" ? e : { general: JSON.stringify(e) });
        return;
      }
      setModal(null);
      load();
      showToast(isEdit ? "Usuario actualizado" : "Usuario creado ✓");
    } catch(e) { showToast(e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete(u) {
    if (!window.confirm(`¿Eliminar usuario "${u.username}"?`)) return;
    try {
      await apiFetch(`/api/core/admin/usuarios/${u.id}/`, { method:"DELETE" });
      load();
      showToast(`Usuario "${u.username}" eliminado`);
    } catch(e) { showToast(e.message, "error"); }
  }

  async function handleToggleActive(u) {
    try {
      await apiFetch(`/api/core/admin/usuarios/${u.id}/`, {
        method:"PATCH",
        body: JSON.stringify({ is_active: !u.is_active })
      });
      load();
      showToast(u.is_active ? `"${u.username}" desactivado` : `"${u.username}" activado`);
    } catch(e) { showToast(e.message, "error"); }
  }

  const f = key => ({ value: form[key] ?? "", onChange: e => setForm(p=>({...p,[key]:e.target.value})) });

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast({msg:"",type:""})}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <span style={{ color:C.muted, fontSize:"0.85rem" }}>{usuarios.length} usuarios registrados</span>
        <Btn onClick={openCreate}><i className="fa-solid fa-user-plus"/>Nuevo usuario</Btn>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:C.muted }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ color:C.accent, marginRight:8 }}/>Cargando…
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {usuarios.map(u => (
            <div key={u.id} style={{ background:C.bg1, border:`1px solid ${C.border}`,
              borderRadius:10, padding:"14px 18px",
              display:"flex", alignItems:"center", gap:14,
              opacity: u.is_active ? 1 : 0.6 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", flexShrink:0,
                background: u.is_superuser ? "rgba(96,165,250,0.15)" : "rgba(249,115,22,0.1)",
                border:`1px solid ${u.is_superuser?"rgba(96,165,250,0.3)":"rgba(249,115,22,0.2)"}`,
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className={`fa-solid fa-${u.is_superuser?"shield-halved":"user"}`}
                   style={{ color: u.is_superuser?"#60a5fa":C.accent }}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontWeight:700, color:C.text, fontSize:"0.92rem" }}>{u.username}</span>
                  {u.is_superuser && (
                    <span style={{ fontSize:"0.65rem", padding:"1px 6px", borderRadius:4,
                      background:"rgba(96,165,250,0.15)", color:"#60a5fa", fontWeight:700 }}>ADMIN</span>
                  )}
                  {!u.is_active && (
                    <span style={{ fontSize:"0.65rem", padding:"1px 6px", borderRadius:4,
                      background:"rgba(239,68,68,0.1)", color:"#f87171", fontWeight:700 }}>INACTIVO</span>
                  )}
                </div>
                <div style={{ color:C.muted, fontSize:"0.77rem", marginTop:2 }}>
                  {u.email || "Sin email"}
                  {u.condominio_nombre && <> · <span style={{ color:"#94a3b8" }}>{u.condominio_nombre}</span></>}
                </div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <Btn small variant="ghost" onClick={()=>handleToggleActive(u)}>
                  <i className={`fa-solid fa-${u.is_active?"toggle-on":"toggle-off"}`}/>
                  {u.is_active?"Activo":"Inactivo"}
                </Btn>
                <Btn small variant="ghost" onClick={()=>openEdit(u)}>
                  <i className="fa-solid fa-pencil"/>Editar
                </Btn>
                <Btn small variant="danger" onClick={()=>handleDelete(u)}>
                  <i className="fa-solid fa-trash"/>
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal==="create"?"Nuevo Usuario":"Editar Usuario"} onClose={()=>setModal(null)}>
          {errors.general && <div style={{ color:"#f87171", marginBottom:12, fontSize:"0.84rem" }}>{errors.general}</div>}

          <Input label="Username" required {...f("username")}
            error={errors.username} placeholder="usuario123"
            disabled={modal !== "create"}/>
          <Input label={modal==="create"?"Contraseña":"Nueva contraseña (dejar vacío para no cambiar)"}
            required={modal==="create"} type={showPw?"text":"password"}
            {...f("password")} error={errors.password}
            placeholder={modal==="create"?"Mínimo 8 caracteres":"Dejar vacío para no cambiar"}/>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer",
              fontSize:"0.8rem", color:C.muted }}>
              <input type="checkbox" checked={showPw} onChange={e=>setShowPw(e.target.checked)}/>
              Mostrar contraseña
            </label>
          </div>
          <Input label="Email" type="email" {...f("email")} error={errors.email} placeholder="usuario@email.com"/>

          <div style={{ marginBottom:14 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer",
              fontSize:"0.8rem", color:C.muted }}>
              <input type="checkbox"
                checked={!!form.is_superuser}
                onChange={e=>setForm(p=>({...p,is_superuser:e.target.checked,condominio_id:e.target.checked?"":p.condominio_id}))}/>
              <span style={{ fontWeight:600, color: form.is_superuser?"#60a5fa":C.muted }}>
                Superadministrador (acceso a todos los condominios)
              </span>
            </label>
          </div>

          {!form.is_superuser && (
            <Select label="Condominio asignado" required error={errors.condominio_id}
              value={form.condominio_id ?? ""}
              onChange={e=>setForm(p=>({...p,condominio_id:e.target.value}))}>
              <option value="">— Seleccionar condominio —</option>
              {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
          )}

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
            <Btn variant="ghost" onClick={()=>setModal(null)}>Cancelar</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>Guardar</>}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Documentos Tab ───────────────────────────────────────────────────────────
const TIPOS_DOC = [
  { value:"asientos",     label:"Asientos de Diario",     cols:["Número","Fecha","Concepto","Tipo"] },
  { value:"facturas",     label:"Facturas CxC",           cols:["Número","Fecha","Filial","Concepto","Monto"] },
  { value:"recibos",      label:"Recibos CxC",            cols:["Número","Fecha","Filial","Monto"] },
  { value:"facturas_cxp", label:"Facturas CxP",           cols:["Número","Fecha","Concepto","Monto"] },
  { value:"recibos_cxp",  label:"Recibos CxP",            cols:["Número","Fecha","Monto"] },
];

function DocumentosTab() {
  const [condominios, setCond]   = useState([]);
  const [condoId,     setCondoId] = useState("");
  const [tipo,        setTipo]    = useState("asientos");
  const [search,      setSearch]  = useState("");
  const [docs,        setDocs]    = useState([]);
  const [total,       setTotal]   = useState(0);
  const [page,        setPage]    = useState(1);
  const [pages,       setPages]   = useState(1);
  const [loading,     setLoading] = useState(false);
  const [selected,    setSelected] = useState(new Set());
  const [deleting,    setDeleting] = useState(false);
  const [toast,       setToast]   = useState({ msg:"", type:"" });

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg:"", type:"" }), 5000);
  };

  useEffect(() => {
    apiFetch("/api/core/admin/condominios/")
      .then(r => r.json()).then(d => setCond(Array.isArray(d) ? d : []));
  }, []);

  const loadDocs = useCallback((p=1) => {
    if (!condoId) return;
    setLoading(true);
    setSelected(new Set());
    apiFetch(`/api/core/admin/documentos/?condominio_id=${condoId}&tipo=${tipo}&search=${encodeURIComponent(search)}&page=${p}`)
      .then(r => r.json())
      .then(d => {
        setDocs(d.results || []);
        setTotal(d.total || 0);
        setPage(d.page || 1);
        setPages(d.pages || 1);
      })
      .finally(() => setLoading(false));
  }, [condoId, tipo, search]);

  useEffect(() => { if (condoId) loadDocs(1); }, [condoId, tipo]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  };

  async function handleDelete() {
    if (!selected.size) { showToast("Seleccione al menos un registro", "error"); return; }
    const tipoLabel = TIPOS_DOC.find(t => t.value === tipo)?.label || tipo;
    if (!window.confirm(
      `¿Eliminar ${selected.size} ${tipoLabel}?

Esta acción no se puede deshacer y eliminará los registros relacionados (CxC, asientos, etc.).`
    )) return;

    setDeleting(true);
    try {
      const res = await apiFetch("/api/core/admin/eliminar-rango/", {
        method: "POST",
        body: JSON.stringify({ condominio_id: condoId, tipo, ids: [...selected] }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || "Error al eliminar", "error"); return; }
      const errMsg = data.errores?.length ? ` (${data.errores.length} errores)` : "";
      showToast(`${data.eliminados} registro(s) eliminados correctamente${errMsg}`);
      setSelected(new Set());
      loadDocs(1);
    } catch(e) { showToast(e.message, "error"); }
    finally { setDeleting(false); }
  }

  const tipoInfo = TIPOS_DOC.find(t => t.value === tipo);
  const fmtMonto = (n) => n != null ? `₡${Number(n).toLocaleString("es-CR")}` : "";

  const cellVal = (doc) => {
    if (tipo === "asientos")     return [doc.numero, doc.fecha, doc.concepto, doc.tipo];
    if (tipo === "facturas")     return [doc.numero, doc.fecha, doc.filial, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos")      return [doc.numero, doc.fecha, doc.filial, fmtMonto(doc.monto)];
    if (tipo === "facturas_cxp") return [doc.numero, doc.fecha, doc.concepto, fmtMonto(doc.monto)];
    if (tipo === "recibos_cxp")  return [doc.numero, doc.fecha, fmtMonto(doc.monto)];
    return [];
  };

  const inpSt = { background:C.bg1, border:`1px solid ${C.border}`, borderRadius:8,
    color:C.text, padding:"9px 14px", fontSize:"0.85rem", outline:"none" };

  return (
    <div>
      <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast({msg:"",type:""})}/>

      {/* Aviso */}
      <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)",
        borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:"0.82rem",
        color:"#fca5a5", display:"flex", alignItems:"center", gap:10 }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ flexShrink:0 }}/>
        Esta función elimina registros de forma permanente e irreversible. Úsela solo cuando el condominio
        lo solicite explícitamente. Los asientos, CxC y movimientos bancarios relacionados también se eliminan.
      </div>

      {/* Filtros */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:12, marginBottom:20 }}>
        <div>
          <label style={{ fontSize:"0.7rem", fontWeight:700, color:C.muted,
            textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:5 }}>
            Condominio
          </label>
          <select value={condoId} onChange={e=>{setCondoId(e.target.value);setDocs([]);setSelected(new Set());}}
            style={{ ...inpSt, width:"100%" }}>
            <option value="">— Seleccionar —</option>
            {condominios.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize:"0.7rem", fontWeight:700, color:C.muted,
            textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:5 }}>
            Tipo de documento
          </label>
          <select value={tipo} onChange={e=>{setTipo(e.target.value);setDocs([]);setSelected(new Set());}}
            style={{ ...inpSt, width:"100%" }}>
            {TIPOS_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize:"0.7rem", fontWeight:700, color:C.muted,
            textTransform:"uppercase", letterSpacing:"0.5px", display:"block", marginBottom:5 }}>
            Buscar
          </label>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&loadDocs(1)}
            placeholder="N° documento o concepto…" style={{ ...inpSt, width:"100%", boxSizing:"border-box" }}/>
        </div>
        <div style={{ display:"flex", alignItems:"flex-end" }}>
          <Btn onClick={()=>loadDocs(1)} disabled={!condoId||loading}>
            <i className={`fa-solid fa-${loading?"circle-notch fa-spin":"magnifying-glass"}`}/>
            Buscar
          </Btn>
        </div>
      </div>

      {/* Tabla */}
      {docs.length > 0 ? (
        <>
          {/* Barra de acciones */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
            marginBottom:12, padding:"10px 14px", background:C.bg1,
            border:`1px solid ${C.border}`, borderRadius:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <label style={{ display:"flex", alignItems:"center", gap:8,
                cursor:"pointer", fontSize:"0.84rem", color:C.muted }}>
                <input type="checkbox"
                  checked={selected.size === docs.length && docs.length > 0}
                  onChange={toggleAll}/>
                Seleccionar todos ({docs.length} en página)
              </label>
              <span style={{ color:C.accent, fontSize:"0.84rem", fontWeight:600 }}>
                {selected.size > 0 && `${selected.size} seleccionados`}
              </span>
              <span style={{ color:C.muted, fontSize:"0.78rem" }}>
                Total: {total} registros
              </span>
            </div>
            <Btn variant="danger" onClick={handleDelete}
              disabled={deleting || selected.size === 0}>
              {deleting
                ? <><i className="fa-solid fa-circle-notch fa-spin"/>Eliminando…</>
                : <><i className="fa-solid fa-trash"/>Eliminar seleccionados ({selected.size})</>}
            </Btn>
          </div>

          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"0.83rem" }}>
              <thead>
                <tr style={{ borderBottom:`2px solid ${C.border}` }}>
                  <th style={{ width:36, padding:"8px 12px" }}/>
                  {tipoInfo?.cols.map(c => (
                    <th key={c} style={{ padding:"8px 12px", textAlign:"left",
                      color:C.muted, fontWeight:700, fontSize:"0.7rem",
                      textTransform:"uppercase", letterSpacing:"0.5px" }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((doc, i) => (
                  <tr key={doc.id}
                    style={{ background: selected.has(doc.id)
                      ? "rgba(249,115,22,0.06)" : i%2===0?"transparent":"rgba(255,255,255,0.02)",
                      borderBottom:`1px solid ${C.border}`, cursor:"pointer" }}
                    onClick={()=>toggleSelect(doc.id)}>
                    <td style={{ padding:"8px 12px", textAlign:"center" }}>
                      <input type="checkbox" checked={selected.has(doc.id)}
                        onChange={()=>toggleSelect(doc.id)}
                        onClick={e=>e.stopPropagation()}/>
                    </td>
                    {cellVal(doc).map((v, ci) => (
                      <td key={ci} style={{ padding:"8px 12px", color: ci===0?C.accent:C.text,
                        fontFamily: ci===0?"monospace":"inherit", maxWidth:250,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {v || "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pages > 1 && (
            <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:16 }}>
              {Array.from({length:pages}, (_,i) => i+1).map(p => (
                <button key={p} onClick={()=>loadDocs(p)}
                  style={{ padding:"5px 12px", borderRadius:6, fontSize:"0.82rem",
                    background: p===page?C.accent:"transparent",
                    color: p===page?"#fff":C.muted,
                    border:`1px solid ${p===page?C.accent:C.border}`,
                    cursor:"pointer" }}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      ) : condoId && !loading ? (
        <div style={{ textAlign:"center", padding:40, color:C.muted }}>
          <i className="fa-solid fa-inbox" style={{ fontSize:"2rem", display:"block", marginBottom:10, opacity:0.2 }}/>
          <p style={{ margin:0 }}>No hay registros. Use el botón Buscar.</p>
        </div>
      ) : !condoId ? (
        <div style={{ textAlign:"center", padding:40, color:C.muted }}>
          <i className="fa-solid fa-building" style={{ fontSize:"2rem", display:"block", marginBottom:10, opacity:0.2 }}/>
          <p style={{ margin:0 }}>Seleccione un condominio para ver sus registros.</p>
        </div>
      ) : null}
    </div>
  );
}

// ── Main AdminPage ────────────────────────────────────────────────────────────
const TABS = [
  { key:"condominios", label:"Condominios", icon:"fa-building"  },
  { key:"usuarios",    label:"Usuarios",    icon:"fa-users"     },
  { key:"documentos",  label:"Eliminar Docs", icon:"fa-trash-can" },
];

export default function AdminPage() {
  const [tab, setTab] = useState("condominios");

  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <span style={{ color:"#9ca3af" }}>
          <Link to="/"><span style={{ color:"#9ca3af" }}>Inicio </span></Link>/ <span>Admin</span>
        </span>
        <h1 style={{ margin:"8px 0 4px", fontSize:"1.7rem", fontWeight:800,
          color:C.text, fontFamily:"'Manrope',sans-serif" }}>
          Panel de Administración
        </h1>
        <p style={{ margin:0, color:C.muted, fontSize:"0.85rem" }}>
          Gestión global de condominios y usuarios — solo accesible para superadministradores
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:3, marginBottom:20,
        background:C.bg2, borderRadius:12, padding:4, border:`1px solid ${C.border}` }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{ flex:1, padding:"10px 16px", borderRadius:9,
                background:active?"rgba(249,115,22,0.1)":"transparent",
                border:active?"1px solid rgba(249,115,22,0.2)":"1px solid transparent",
                color:active?C.accent:C.muted,
                fontWeight:active?700:400, fontSize:"0.85rem",
                cursor:"pointer", display:"flex", alignItems:"center",
                justifyContent:"center", gap:8, transition:"all 0.15s",
                fontFamily:"'Inter',sans-serif" }}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize:"0.8rem" }}/>{t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:14, padding:"22px 24px" }}>
        {tab === "condominios" && <CondominiosTab/>}
        {tab === "usuarios"    && <UsuariosTab/>}
        {tab === "documentos"  && <DocumentosTab/>}
      </div>
    </div>
  );
}
